import clsx from 'clsx'
import { useState } from 'react'

import { usePostSignedUrl } from 'api/mutations/usePostSignedUrl'
import { useUpdateUser } from 'api/mutations/useUpdateUser'
import { useFileById } from 'api/queries/matchAndThrive/useFileById'
import { useMe } from 'api/queries/matchAndThrive/useMe'
import { ReactComponent as CrossIcon } from 'assets/icons/close-icon.svg'
import { ReactComponent as DocumentIcon } from 'assets/icons/file.svg'
import { FileUpload } from 'components/uikit/FileUpload/FileUpload'
import { Icon } from 'components/uikit/Icon/Icon'
import { Skeleton } from 'components/uikit/Skeleton/Skeleton'
import { fileUpload } from 'helpers/fileUpload'
import { useGetUserData } from 'hooks/getUserData'

import styles from './PersistentPlatform.module.scss'

interface FileItemType extends File {
  [key: string]: unknown
}

export default function PersistentPlatformFileUpload() {
  const [isUploading, setIsUploading] = useState(false)
  const { email: user_email } = useGetUserData()
  const { data: me, isLoading: isLoadingMe } = useMe()
  const { data: file, isLoading: isLoadingFile } = useFileById({
    params: {
      fileId: me?.persistentPlatformFile?.id || '',
    },
    enabled: !!me?.persistentPlatformFile,
  })
  const { mutateAsync: postSignedUrl, isPending: isPendingSignedUrl } = usePostSignedUrl()
  const { mutateAsync: updateUser, isPending: isPendingMe } = useUpdateUser()

  const handleFileUpload = async (file?: FileItemType) => {
    if (!file) {
      return
    }
    setIsUploading(true)
    try {
      const urlResponse = await postSignedUrl({
        description: 'Persistent Platform File',
        extension: 'pptx',
        mimeType: 'application/vnd.openxmlformats-officedocument.presentationml.presentation',
        name: file.name,
        originalName: file.name,
        size: file.size,
        uploadType: 'users',
        metaData: JSON.stringify({}),
        type: 'file',
        currentIndex: 0,
      })
      const urlResponseData = urlResponse.data
      await fileUpload(urlResponseData, file as FileItemType)
      await updateUser({
        email: user_email,
        persistentPlatformFile: { id: urlResponseData.fileObject.id },
      })
    } finally {
      setIsUploading(false)
    }
  }

  if (isUploading || isLoadingMe || isLoadingFile || isPendingMe || isPendingSignedUrl) {
    return <Skeleton data-testid="skeleton" className="h-[28px] rounded-[16px] w-full" />
  }

  if (!me?.persistentPlatformFile || !file) {
    return (
      <FileUpload
        data-testid="file-upload"
        acceptConfig={{ 'application/vnd.openxmlformats-officedocument.presentationml.presentation': ['.pptx'] }}
        onChange={file => handleFileUpload(file as FileItemType | undefined)}
        maxFiles={1}
        className={clsx(styles.fileUpload, 'max-h-[146px]')}
      />
    )
  }

  return (
    <div className="flex flex-row items-center justify-between gap-x-3 bg-white rounded-xl p-3">
      <div className="flex flex-row items-center gap-x-4">
        {/* icon file */}
        <Icon icon={DocumentIcon} color="black" />
        {/* file name & file size*/}
        <div className="flex flex-col">
          <div
            className="text-[14px] underline font-bold text-[#006B3F] hover:cursor-pointer"
            onClick={() => {
              window.open(file?.downloadUrl, '_blank')
            }}
          >
            {file?.originalName || me?.persistentPlatformFile?.originalName}
          </div>
          <div className="text-[12px] font-normal text-black">
            {file?.size ? (file.size / (1024 * 1024)).toFixed(2) : '0'} MB
          </div>
        </div>
      </div>
      {/* Close button */}
      <button
        data-testid="close-button"
        className="hover:cursor-pointer"
        onClick={() => updateUser({ email: user_email, persistentPlatformFile: null })}
      >
        <Icon icon={CrossIcon} color="black" />
      </button>
    </div>
  )
}
